// src/pages/Fees.js
// ──────────────────────────────────────────────────────────────────────────
// Fees & tuition. Reads editable content from site_content.fees:
//   { intro: '…', pdf: 'https://…media…/fees.pdf' }
// Falls back to a short default + the static /fees/tuition-fees.pdf path.
// ──────────────────────────────────────────────────────────────────────────

import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useSiteContent } from '../context/SiteContentContext';
import theme from '../theme/theme';

const Header = styled.header`
  background: ${theme.color.blueDeep}; color: #fff; padding: 7rem ${theme.layout.gutter} 5rem;
  .inner { max-width: ${theme.layout.maxWidth}; margin: 0 auto; }
  h1 { font-weight: 400; font-size: clamp(2.4rem, 6vw, 4rem); color: #fff; margin-top: 1.2rem; }
  p { margin-top: 1rem; max-width: 54ch; font-weight: 300; color: rgba(255,255,255,0.82); }
  .kicker { color: #fff; }
`;
const Section = styled.section` max-width: ${theme.layout.maxWidth}; margin: 0 auto; padding: 5rem ${theme.layout.gutter}; `;
const Card = styled.div`
  border: 1px solid ${theme.color.line}; border-radius: ${theme.layout.radius}; padding: 2.4rem; background: #fff;
  display: flex; align-items: center; justify-content: space-between; gap: 2rem; flex-wrap: wrap;
  .doc { display: flex; align-items: center; gap: 1rem; }
  .ic { width: 52px; height: 52px; border-radius: 10px; background: ${theme.color.lemonSoft}; color: ${theme.color.accent};
        display: grid; place-items: center; font-weight: 800; font-size: 0.8rem; }
  h3 { font-weight: 500; font-size: 1.2rem; } .doc p { margin: 0.2rem 0 0; color: ${theme.color.inkSoft}; font-size: 0.9rem; }
  a.btn { background: ${theme.color.accent}; color: #fff; font-weight: 700; padding: 13px 24px; border-radius: ${theme.layout.radius}; white-space: nowrap; }
  a.btn:hover { background: ${theme.color.accentDark}; }
`;
const Lead = styled.p` font-size: 1.05rem; color: ${theme.color.inkSoft}; max-width: 60ch; margin: 0 0 2.5rem; `;

const InfoGrid = styled.div`
  display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 1.4rem; margin-top: 2rem;
  @media (max-width: 860px) { grid-template-columns: 1fr; }
`;
const InfoCard = styled.div`
  border: 1px solid ${theme.color.line}; border-radius: ${theme.layout.radius}; padding: 1.6rem; background: ${theme.color.paperAlt};
  h3 { font-weight: 600; font-size: 1.15rem; margin-bottom: 0.8rem; }
  p { color: ${theme.color.inkSoft}; margin: 0.45rem 0; }
  strong { color: ${theme.color.ink}; }
`;
const Steps = styled.ol`
  margin: 1rem 0 0; padding-left: 1.2rem; color: ${theme.color.inkSoft};
  li { margin: 0.55rem 0; }
  strong { color: ${theme.color.ink}; }
`;
const TableBlock = styled.div` margin-top: 3rem; `;
const TableTitle = styled.div`
  display: flex; align-items: end; justify-content: space-between; gap: 1.5rem; flex-wrap: wrap; margin-bottom: 1rem;
  h2 { font-size: clamp(1.6rem, 3vw, 2.1rem); font-weight: 500; }
  p { color: ${theme.color.inkSoft}; margin: 0; max-width: 58ch; }
`;
const TableWrap = styled.div`
  overflow-x: auto; border: 1px solid ${theme.color.line}; border-radius: ${theme.layout.radius}; background: #fff;
`;
const FeeTable = styled.table`
  width: 100%; border-collapse: collapse; min-width: 760px;
  th, td { padding: 0.95rem 1rem; text-align: left; border-bottom: 1px solid ${theme.color.line}; vertical-align: top; }
  th { background: ${theme.color.blueDeep}; color: #fff; font-size: 0.78rem; text-transform: uppercase; letter-spacing: 0.08em; }
  tbody tr:last-child td { border-bottom: 0; }
  td { color: ${theme.color.inkSoft}; }
  td:first-child, td:nth-child(2), .fee { color: ${theme.color.ink}; font-weight: 600; }
  .fee { white-space: nowrap; }
`;
const Notes = styled.div`
  margin-top: 1rem; padding: 1rem 1.2rem; border-left: 3px solid ${theme.color.lemon}; background: ${theme.color.lemonSoft}; color: ${theme.color.onLemon};
  p { margin: 0.25rem 0; }
`;

const individualIctCourses = [
  { no: '1', course: 'Word', level: 'Basic', duration: '1 week (5 sessions)', topics: 'Typing, formatting, saving, editing, page layout', fee: 'ZMW 300 – 400' },
  { no: '', course: 'Word', level: 'Intermediate', duration: '1 week (5 sessions)', topics: 'Tables, styles, mail merge, templates', fee: 'ZMW 400 – 500' },
  { no: '', course: 'Word', level: 'Advanced', duration: '1 week (5 sessions)', topics: 'Table of contents, macros, advanced formatting', fee: 'ZMW 500 – 600' },
  { no: '2', course: 'Excel', level: 'Basic', duration: '1 week (5 sessions)', topics: 'Data entry, formulas, charts, formatting', fee: 'ZMW 350 – 450' },
  { no: '', course: 'Excel', level: 'Intermediate', duration: '1 week (5 sessions)', topics: 'Functions, conditional formatting, sorting, filtering', fee: 'ZMW 450 – 550' },
  { no: '', course: 'Excel', level: 'Advanced', duration: '1 week (5 sessions)', topics: 'Pivot tables, dashboards, macros, data validation', fee: 'ZMW 550 – 700' },
  { no: '3', course: 'PowerPoint', level: 'Basic', duration: '3 days', topics: 'Slides creation, themes, transitions', fee: 'ZMW 250 – 350' },
  { no: '', course: 'PowerPoint', level: 'Intermediate', duration: '3 days', topics: 'Animations, custom shows, media', fee: 'ZMW 300 – 400' },
  { no: '', course: 'PowerPoint', level: 'Advanced', duration: '3 days', topics: 'Master slides, interactivity, exporting to video', fee: 'ZMW 350 – 500' },
  { no: '4', course: 'Outlook', level: 'Basic–Intermediate', duration: '2 days', topics: 'Email use, folders, rules, calendar', fee: 'ZMW 300 – 400' },
  { no: '5', course: 'Access', level: 'Introductory', duration: '1 week (5 sessions)', topics: 'Tables, forms, queries, reports', fee: 'ZMW 500 – 700' },
];

const ictPackages = [
  { no: '1', name: 'Office Essentials', included: 'Word, Excel, PowerPoint (Basic)', duration: '3 weeks (15 sessions)', details: 'For students, job seekers, office users', fee: 'ZMW 800 – 1,000' },
  { no: '2', name: 'Professional Package', included: 'Word, Excel, PowerPoint (Intermediate & Advanced)', duration: '4 weeks (20 sessions)', details: 'For professionals seeking productivity mastery', fee: 'ZMW 1,200 – 1,500' },
  { no: '3', name: 'Full Microsoft Office Suite', included: 'Word, Excel, PowerPoint, Outlook, Access', duration: '6–8 weeks', details: 'Covers all levels; ideal for certification or office careers', fee: 'ZMW 3,200' },
  { no: '4', name: 'Access (Database)', included: 'Intermediate & Advanced', duration: '3 months', details: 'Data management and analysis', fee: 'ZMW 3,200' },
  { no: '5', name: 'Web Development (HTML)', included: 'Intermediate & Advanced', duration: '3 months', details: 'Practical web page structure and HTML foundations', fee: 'Enquire' },
];


function Fees() {
  const fees = useSiteContent('fees') || {};
  const pdf = fees.pdf || '';
  const intro = fees.intro
    || 'Our fees are kept affordable and vary by course. Download the current fee structure below, '
     + 'or get in touch — we’ll also help you explore CDF, donor and sponsorship funding.';

  return (
    <>
      <Navbar />
      <Header>
        <div className="inner">
          <span className="kicker">Fees &amp; tuition</span>
          <h1>What it costs to train</h1>
          <p>Clear, affordable fees — with funding routes if you need them.</p>
        </div>
      </Header>

      <Section>
        <Lead>{intro}</Lead>
        <Card>
          <div className="doc">
            <div className="ic">PDF</div>
            <div>
              <h3>Fee structure</h3>
              <p>{pdf ? 'Current tuition and course fees' : 'Being prepared — please check back soon'}</p>
            </div>
          </div>
          {pdf
            ? <a className="btn" href={pdf} target="_blank" rel="noreferrer">Download fees (PDF) ↗</a>
            : <Link className="btn" to="/contact">Ask about fees</Link>}
        </Card>

        <InfoGrid>
          <InfoCard>
            <span className="kicker">Payment details</span>
            <h3>School fees bank account</h3>
            <p><strong>Account number:</strong> 0216415549018</p>
            <p><strong>Account name:</strong> CGA MBALA TRAINING LTD</p>
            <p>Please confirm the account name before completing payment.</p>
          </InfoCard>

          <InfoCard>
            <span className="kicker">Mobile payment guide</span>
            <h3>Airtel Money to bank account</h3>
            <Steps>
              <li>Open the <strong>Send Money</strong> menu.</li>
              <li>Select <strong>4. Banks</strong>.</li>
              <li>Select <strong>1. Transfer to bank</strong>.</li>
              <li>Select the bank linked to the institute account. Use <strong>Next</strong> if the bank is not on the first list.</li>
              <li>Enter account number <strong>0216415549018</strong>.</li>
              <li>Confirm <strong>CGA MBALA TRAINING LTD</strong>, enter the amount, then approve with your PIN.</li>
            </Steps>
          </InfoCard>
        </InfoGrid>

        <TableBlock>
          <TableTitle>
            <div>
              <span className="kicker">ICT department</span>
              <h2>Individual ICT course fees</h2>
            </div>
            <p>Prices and duration for Microsoft Office application training.</p>
          </TableTitle>
          <TableWrap>
            <FeeTable>
              <thead>
                <tr><th>S/N</th><th>Course</th><th>Level</th><th>Duration</th><th>Topics covered</th><th>Fee</th></tr>
              </thead>
              <tbody>
                {individualIctCourses.map((item, index) => (
                  <tr key={`${item.course}-${item.level}-${index}`}>
                    <td>{item.no}</td><td>{item.course}</td><td>{item.level}</td><td>{item.duration}</td><td>{item.topics}</td><td className="fee">{item.fee}</td>
                  </tr>
                ))}
              </tbody>
            </FeeTable>
          </TableWrap>
        </TableBlock>

        <TableBlock>
          <TableTitle>
            <div>
              <span className="kicker">ICT packages</span>
              <h2>Full ICT packages and programmes</h2>
            </div>
            <p>Packages combine key ICT skills for school, work and business productivity.</p>
          </TableTitle>
          <TableWrap>
            <FeeTable>
              <thead>
                <tr><th>S/N</th><th>Package name</th><th>Included courses</th><th>Duration</th><th>Details</th><th>Fee</th></tr>
              </thead>
              <tbody>
                {ictPackages.map((item) => (
                  <tr key={item.no}>
                    <td>{item.no}</td><td>{item.name}</td><td>{item.included}</td><td>{item.duration}</td><td>{item.details}</td><td className="fee">{item.fee}</td>
                  </tr>
                ))}
              </tbody>
            </FeeTable>
          </TableWrap>
          <Notes>
            <p>Corporate ICT training is offered by highly qualified professionals.</p>
            <p>Certificate printing fee: ZMW 100 per learner.</p>
            <p>Group discounts are available for institutions or 5+ learners.</p>
            <p>Session times can be adjusted to fit learners, for example 1–2 hours per day.</p>
          </Notes>
        </TableBlock>

        <p style={{ marginTop: '2rem', color: theme.color.inkSoft }}>
          Looking for help paying? See <Link to="/scholarships" style={{ color: theme.color.accent, fontWeight: 600 }}>scholarships &amp; sponsorship</Link>,
          or <Link to="/apply" style={{ color: theme.color.accent, fontWeight: 600 }}>start your application</Link>.
        </p>
      </Section>

      <Footer />
    </>
  );
}

export default Fees;
